import { create } from 'zustand';
import { User, CompanyMembership } from '@/types';
import authService from '@/services/auth.service';
import { queryClient } from '@/config/queryClient';
import { mergeUserClaimsFromToken } from '@/utils/jwtPermissions';
import { withPermissionAliases } from '@/utils/permissionAliases';
import {
  connectNotificationSocket,
  disconnectNotificationSocket,
} from '@/services/notificationSocket.service';

/** If access JWT is still valid for longer than this, skip proactive refresh (reduces /auth/refresh-token load). */
const PROACTIVE_REFRESH_MIN_REMAINING_SEC = 8 * 60;

function shouldProactivelyRefreshAccessToken(accessToken: string | null): boolean {
  if (!accessToken?.trim()) return true;
  try {
    const seg = accessToken.split('.')[1];
    if (!seg) return true;
    let b64 = seg.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    const json = atob(b64);
    const payload = JSON.parse(json) as { exp?: number };
    if (typeof payload.exp !== 'number') return true;
    const expMs = payload.exp * 1000;
    return expMs < Date.now() + PROACTIVE_REFRESH_MIN_REMAINING_SEC * 1000;
  } catch {
    return true;
  }
}

function applySessionUser(user: User, token: string): User {
  const userWithClaims = mergeUserClaimsFromToken(user, token);
  if (!userWithClaims.permissions?.length && Array.isArray(user.permissions)) {
    userWithClaims.permissions = user.permissions;
  }
  if (!userWithClaims.roles?.length && Array.isArray(user.roles)) {
    userWithClaims.roles = user.roles;
  }
  if (user.memberships?.length && !userWithClaims.memberships?.length) {
    userWithClaims.memberships = user.memberships;
  }
  if (user.parent_company_id && !userWithClaims.parent_company_id) {
    userWithClaims.parent_company_id = user.parent_company_id;
  }
  return withPermissionAliases(userWithClaims);
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  /** Pending multi-company login selection (not yet fully authenticated). */
  pendingCompanySelection: {
    selection_token: string;
    companies: CompanyMembership[];
  } | null;

  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  /** Returns true if session established; false if company selection required. */
  login: (username: string, password: string) => Promise<boolean>;
  completeCompanySelection: (companyId: string) => Promise<void>;
  switchCompany: (companyId: string) => Promise<void>;
  clearPendingCompanySelection: () => void;
  logout: () => Promise<void>;
  initializeAuth: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  pendingCompanySelection: null,

  setUser: (rawUser) => {
    const user = withPermissionAliases(rawUser);
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
    set({
      user,
      isAuthenticated: !!user,
      error: null,
    });
  },

  setLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error, isLoading: false }),

  clearPendingCompanySelection: () => set({ pendingCompanySelection: null }),

  login: async (username, password) => {
    try {
      set({ isLoading: true, error: null, pendingCompanySelection: null });

      const result = await authService.login({ username, password });

      if (result.requires_company_selection) {
        set({
          isLoading: false,
          isAuthenticated: false,
          user: null,
          pendingCompanySelection: {
            selection_token: result.selection_token,
            companies: result.companies,
          },
        });
        return false;
      }

      authService.saveTokens(result.token, result.refreshToken);
      const userWithClaims = applySessionUser(
        { ...result.user, memberships: result.memberships || result.user.memberships },
        result.token
      );
      localStorage.setItem('user', JSON.stringify(userWithClaims));
      queryClient.clear();
      disconnectNotificationSocket();
      connectNotificationSocket(result.token);

      set({
        user: userWithClaims,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        pendingCompanySelection: null,
      });
      return true;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Login failed';
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: errorMessage,
        pendingCompanySelection: null,
      });
      throw error;
    }
  },

  completeCompanySelection: async (companyId: string) => {
    const pending = get().pendingCompanySelection;
    if (!pending?.selection_token) {
      throw new Error('No company selection in progress');
    }
    set({ isLoading: true, error: null });
    try {
      const result = await authService.selectCompany(pending.selection_token, companyId);
      authService.saveTokens(result.token, result.refreshToken);
      const userWithClaims = applySessionUser(
        { ...result.user, memberships: result.memberships || result.user.memberships },
        result.token
      );
      localStorage.setItem('user', JSON.stringify(userWithClaims));
      queryClient.clear();
      disconnectNotificationSocket();
      connectNotificationSocket(result.token);
      set({
        user: userWithClaims,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        pendingCompanySelection: null,
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Company selection failed';
      set({ isLoading: false, error: errorMessage });
      throw error;
    }
  },

  switchCompany: async (companyId: string) => {
    set({ isLoading: true, error: null });
    try {
      const result = await authService.switchCompany(companyId);
      authService.saveTokens(result.token, result.refreshToken);
      const userWithClaims = applySessionUser(
        { ...result.user, memberships: result.memberships || result.user.memberships },
        result.token
      );
      localStorage.setItem('user', JSON.stringify(userWithClaims));
      queryClient.clear();
      disconnectNotificationSocket();
      connectNotificationSocket(result.token);
      set({
        user: userWithClaims,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to switch company';
      set({ isLoading: false, error: errorMessage });
      throw error;
    }
  },

  logout: async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error', error);
    } finally {
      disconnectNotificationSocket();
      authService.clearTokens();
      localStorage.removeItem('user');
      queryClient.clear();
      set({
        user: null,
        isAuthenticated: false,
        error: null,
        pendingCompanySelection: null,
      });
    }
  },

  initializeAuth: async () => {
    const token = authService.getToken();
    const userStr = localStorage.getItem('user');

    if (!token || !userStr) {
      set({ isAuthenticated: false, user: null, isLoading: false });
      return;
    }

    try {
      set({ isLoading: true });

      const parsed = JSON.parse(userStr) as User;
      const tokenCurrent = authService.getToken();
      const user = withPermissionAliases(mergeUserClaimsFromToken(parsed, tokenCurrent));
      if (JSON.stringify(user) !== userStr) {
        localStorage.setItem('user', JSON.stringify(user));
      }

      try {
        const profile = await authService.getCurrentUser();
        const merged = withPermissionAliases({
          ...user,
          ...profile,
          permissions: profile.permissions?.length ? profile.permissions : user.permissions,
          roles: profile.roles?.length ? profile.roles : user.roles,
          is_super_admin: profile.is_super_admin ?? user.is_super_admin,
          memberships: profile.memberships?.length ? profile.memberships : user.memberships,
          parent_company_id: profile.parent_company_id ?? user.parent_company_id,
        });
        localStorage.setItem('user', JSON.stringify(merged));
        connectNotificationSocket(tokenCurrent || token);
        set({
          user: merged,
          isAuthenticated: true,
          isLoading: false,
        });
      } catch {
        connectNotificationSocket(tokenCurrent || token);
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
        });
      }
    } catch (error) {
      console.error('[AuthStore] Failed to initialize auth', error);
      authService.clearTokens();
      localStorage.removeItem('user');
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  },

  refreshSession: async () => {
    const refresh = authService.getRefreshToken();
    if (!refresh) return;
    const access = authService.getToken();
    if (!shouldProactivelyRefreshAccessToken(access)) return;
    try {
      const tokens = await authService.refreshToken(refresh);
      authService.saveTokens(tokens.token, tokens.refreshToken);
      const raw = localStorage.getItem('user');
      if (!raw) return;
      const parsed = JSON.parse(raw) as User;
      const merged = mergeUserClaimsFromToken(parsed, tokens.token);
      try {
        const profile = await authService.getCurrentUser();
        Object.assign(merged, {
          ...profile,
          permissions: profile.permissions?.length ? profile.permissions : merged.permissions,
          roles: profile.roles?.length ? profile.roles : merged.roles,
          memberships: profile.memberships?.length ? profile.memberships : merged.memberships,
        });
      } catch {
        /* keep JWT merge */
      }
      merged.permissions = withPermissionAliases(merged).permissions;
      localStorage.setItem('user', JSON.stringify(merged));
      disconnectNotificationSocket();
      connectNotificationSocket(tokens.token);
      set({ user: merged, isAuthenticated: true });
    } catch (e) {
      console.error('[AuthStore] refreshSession failed', e);
    }
  },
}));
